// Stores the offline-access `presentaConnectionId` a viewer has been granted,
// keyed by resourceId, so a user who has unlocked several shared resources
// doesn't have to re-verify each time — and so the id never has to live in the
// URL (which would let anyone reuse someone else's connection).

const STORAGE_KEY = "offlineAccessConnections";

export interface OfflineAccessConnection {
  resourceId: string;
  presentaConnectionId: string;
}

type OfflineAccessConnectionMap = Record<string, OfflineAccessConnection>;

const readMap = (): OfflineAccessConnectionMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as OfflineAccessConnectionMap) : {};
  } catch {
    return {};
  }
};

const writeMap = (map: OfflineAccessConnectionMap) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
};

/** Persist (or update) the connection for a resource. */
export const saveOfflineAccessConnection = (
  resourceId: string,
  presentaConnectionId: string,
) => {
  if (!resourceId || !presentaConnectionId) return;
  const map = readMap();
  map[resourceId] = { resourceId, presentaConnectionId };
  writeMap(map);
};

/** Get the stored connection id for a resource, or "" if none. */
export const getOfflineAccessConnectionId = (resourceId: string): string =>
  readMap()[resourceId]?.presentaConnectionId ?? "";

/** Remove a single resource's connection. */
export const removeOfflineAccessConnection = (resourceId: string) => {
  const map = readMap();
  if (map[resourceId]) {
    delete map[resourceId];
    writeMap(map);
  }
};

/** Clear every stored offline-access connection (used on logout). */
export const clearOfflineAccessConnections = () => {
  localStorage.removeItem(STORAGE_KEY);
};
